import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useDuplicatePipeline } from '../hooks/use-pipelines';
import { toast } from 'sonner';

interface DuplicatePipelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourcePipeline: {
    id: string;
    name: string;
  } | null;
}

export function DuplicatePipelineDialog({
  open,
  onOpenChange,
  sourcePipeline,
}: DuplicatePipelineDialogProps) {
  const navigate = useNavigate();
  const duplicatePipeline = useDuplicatePipeline();

  const [name, setName] = useState('');
  const [copyHistory, setCopyHistory] = useState(false);

  const isPending = duplicatePipeline.isPending;

  // Set default name when source pipeline changes
  useEffect(() => {
    if (sourcePipeline) {
      const defaultName = `${sourcePipeline.name} (Copy)`;
      setName(defaultName);
    }
  }, [sourcePipeline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourcePipeline || !name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }

    try {
      const result = await duplicatePipeline.mutateAsync({
        pipelineId: sourcePipeline.id,
        name: name.trim(),
        copyHistory,
      });

      onOpenChange(false);
      toast.success(`Pipeline duplicated as "${result.name}"`);

      // Navigate to the new pipeline
      navigate(`/pipelines/${result.id}`);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen || !isPending) {
      onOpenChange(newOpen);
      if (!newOpen) {
        // Reset form after a short delay to avoid UI flicker
        setTimeout(() => {
          setCopyHistory(false);
        }, 100);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Duplicate Pipeline</DialogTitle>
            <DialogDescription>
              Create a copy of <strong>"{sourcePipeline?.name}"</strong>. You can modify
              the name and choose whether to include execution history.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Pipeline Name */}
            <div className="space-y-2">
              <Label htmlFor="duplicate-name">
                New Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="duplicate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter pipeline name..."
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Copy History Option */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="copy-history">Copy execution history</Label>
                <p className="text-xs text-muted-foreground">
                  Include past runs in the duplicated pipeline
                </p>
              </div>
              <Switch
                id="copy-history"
                checked={copyHistory}
                onCheckedChange={setCopyHistory}
                disabled={isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Duplicating...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
